import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Trophy } from 'lucide-react';
import { SPONSORS, getMainSponsors } from '../data/sponsors';
import { BONUS_SPONSORS } from '../data/sponsors';
import { SponsorCard } from '../components/SponsorCard';
import { motion, AnimatePresence } from 'framer-motion';

const Home = () => {
  const mainSponsors = getMainSponsors();
  const vipSponsors = SPONSORS.filter(s => s.tier === 'vip');
  const allSponsors = [...SPONSORS, ...BONUS_SPONSORS];
  const [featuredIndex, setFeaturedIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % allSponsors.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [allSponsors.length]);

  const featuredSponsor = allSponsors[featuredIndex];
  const isBonus = featuredSponsor.tier === 'bonus';

  return (
    <div>
      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-bg" />
        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="hero-title" data-testid="hero-title">
              <span className="accent">BetMax</span> Gaming<br />Solution's
            </h1>
            <p className="hero-subtitle">
              En guvenilir bahis ve casino platformlarina tek bir yerden ulasin.
              Ozel teklifler ve bonuslar icin hemen kesfetmeye baslayin.
            </p>
            <div className="hero-ctas">
              <Link
                to="/sponsorlar"
                className="sponsor-visit-btn"
                style={{ width: 'auto' }}
                data-testid="hero-primary-cta-button"
              >
                Sponsorlari Gor <ArrowRight size={16} />
              </Link>
              <Link
                to="/iletisim"
                className="contact-btn contact-btn-secondary"
                data-testid="hero-contact-cta-button"
              >
                Iletisim
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="hero-featured"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={featuredSponsor.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <SponsorCard sponsor={featuredSponsor} buttonText={isBonus ? "KAYIT OL!" : "KAYDOL, 2500TL KAZAN!"} />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <div style={{
        background: 'rgba(0,0,0,0.3)',
        borderTop: '1px solid rgba(0,194,255,0.08)',
        borderBottom: '1px solid rgba(0,194,255,0.08)',
        padding: '1.5rem 1rem'
      }}>
        <div style={{
          maxWidth: '72rem',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Star size={20} style={{ color: 'hsl(200, 96%, 60%)', margin: '0 auto 0.3rem' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', color: 'hsl(210, 20%, 96%)' }}>8+</div>
            <div style={{ fontSize: '0.8rem', color: 'hsl(215, 12%, 70%)' }}>Partner Platform</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Shield size={20} style={{ color: 'hsl(200, 96%, 60%)', margin: '0 auto 0.3rem' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', color: 'hsl(210, 20%, 96%)' }}>%100</div>
            <div style={{ fontSize: '0.8rem', color: 'hsl(215, 12%, 70%)' }}>Guvenilir</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Trophy size={20} style={{ color: 'hsl(200, 96%, 60%)', margin: '0 auto 0.3rem' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', color: 'hsl(210, 20%, 96%)' }}>7/24</div>
            <div style={{ fontSize: '0.8rem', color: 'hsl(215, 12%, 70%)' }}>Destek</div>
          </div>
        </div>
      </div>

      {/* ANA SPONSORLAR */}
      <section style={{ padding: '3rem 0' }}>
        <div className="page-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span className="tier-badge tier-badge-main">Ana Sponsorlar</span>
            </div>
            <h2 className="section-title">Ana Sponsorlarimiz</h2>
            <p className="section-subtitle">En guvenilir ve populer partner platformlarimiz</p>
          </motion.div>
          <div className="sponsors-grid">
            {mainSponsors.map((sponsor, idx) => (
              <motion.div
                key={sponsor.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <SponsorCard sponsor={sponsor} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP SPONSORLAR */}
      <section style={{ padding: '3rem 0' }}>
        <div className="page-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span className="tier-badge tier-badge-vip">VIP Sponsorlar</span>
            </div>
            <h2 className="section-title">VIP Sponsorlarimiz</h2>
            <p className="section-subtitle">Ozel ayricaliklari ile one cikan platformlar</p>
          </motion.div>
          <div className="sponsors-grid">
            {vipSponsors.map((sponsor, idx) => (
              <motion.div
                key={sponsor.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <SponsorCard sponsor={sponsor} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* YUKSEK ODEME GARANTISI */}
      <section style={{ padding: '3rem 0' }}>
        <div className="page-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span className="tier-badge tier-badge-bonus">Yuksek Odeme Garantisi</span>
            </div>
            <h2 className="section-title">Yuksek Odeme Garantisi</h2>
            <p className="section-subtitle">En yuksek odeme oranlarini sunan guvenilir platformlar</p>
          </motion.div>
          <div className="sponsors-grid">
            {BONUS_SPONSORS.map((sponsor, idx) => (
              <motion.div
                key={sponsor.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <SponsorCard sponsor={sponsor} buttonText="KAYIT OL!" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
